import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { useInsightsAccess } from './useInsightsAccess';
import { isAllowedGapsLens } from './useLens';

// Shared time-period state for the Insights dashboards. Persists in the URL
// (?from=YYYY-MM-DD&to=YYYY-MM-DD&period=3m) so the browser back/forward
// buttons restore the user's range as part of the same history step that
// changed it.

export type PresetId =
  | '1w'
  | '1m'
  | '3m'
  | '6m'
  | 'term'
  | 'ytd'
  | '1y'
  | 'all'
  | 'custom';

export type TimePeriod = {
  from: string | null;
  to: string | null;
  preset: PresetId;
};

export const DEFAULT_PRESET: PresetId = 'term';

export const PRESET_IDS: PresetId[] = [
  '1w',
  '1m',
  '3m',
  '6m',
  'term',
  'ytd',
  '1y',
  'all',
  'custom',
];

// Labels for the fixed-window presets. `term` is deliberately absent: it has
// no fixed name, it's always "Since <the date it starts from>", so its label
// is generated in `presetLabel` from that date instead of written out here.
const FIXED_PRESET_LABELS: Record<Exclude<PresetId, 'term'>, string> = {
  '1w': 'Last week',
  '1m': 'Last month',
  '3m': 'Last 3 months',
  '6m': 'Last 6 months',
  ytd: 'Since Jan 1',
  '1y': 'Last year',
  all: 'All time',
  custom: 'Custom range',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Format a Date as YYYY-MM-DD in the user's local timezone. Using
// `toISOString()` would emit UTC, which silently shifts the from/to window
// by a day for US admins on the wrong side of midnight UTC.
function isoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Symmetric inverse of `isoDate`: parse a 'YYYY-MM-DD' string as the
// matching local calendar day (anchored at noon to dodge DST). Using
// `new Date(iso)` parses as UTC midnight, which shifts west-of-UTC users
// back one day when re-formatted with `toLocaleDateString` — the
// dashboard period labels and the histogram bars both desync as a
// result.
function parseLocalDay(iso: string): Date {
  const [yearStr, monthStr, dayStr] = iso.split('-');
  return new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr), 12, 0, 0, 0);
}

// The school year splits in two: the fall half opens Aug 1, the spring half
// Jan 1. The dashboard default starts at whichever of those the viewer is
// currently in, so an admin never opens on a window that mixes in the
// previous half's roster. Built as a literal string rather than through a
// Date so it can't drift a day in either direction the way `toISOString()`
// does.
const FALL_TERM_START_MONTH = 7; // August, 0-indexed

export function termStartIso(today: Date): string {
  const year = today.getFullYear();
  return today.getMonth() >= FALL_TERM_START_MONTH ? `${year}-08-01` : `${year}-01-01`;
}

// 'term' is never named as a period ("this semester", "this term") — it's
// always "Since <the date it starts from>", generated from that date so the
// label can't disagree with the window it describes. Every UI call site goes
// through here.
export function presetLabel(preset: PresetId, today = new Date()): string {
  if (preset !== 'term') return FIXED_PRESET_LABELS[preset];
  const start = parseLocalDay(termStartIso(today));
  return `Since ${start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;
}

export function presetRange(
  preset: PresetId,
): { from: string | null; to: string | null } {
  const today = new Date();
  const to = isoDate(today);
  if (preset === 'all') return { from: null, to: null };
  if (preset === 'custom') return { from: null, to: null };
  if (preset === 'term') {
    return { from: termStartIso(today), to };
  }
  if (preset === 'ytd') {
    return { from: `${today.getFullYear()}-01-01`, to };
  }
  const days =
    preset === '1w'
      ? 7
      : preset === '1m'
      ? 30
      : preset === '3m'
      ? 90
      : preset === '6m'
      ? 180
      : 365;
  // Both ends are inclusive and today is one of the days, so "Last week" on
  // Oct 15 starts Oct 9, not Oct 8.
  const from = isoDate(new Date(today.getTime() - (days - 1) * MS_PER_DAY));
  return { from, to };
}

// Build a `?key=value&…` suffix from the current period state so internal
// links can carry the user's range across navigation. Returns `""` when on
// the default preset so default URLs stay clean.
export function periodQuerySuffix(
  period: TimePeriod,
  extra: Record<string, string> = {},
): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(extra)) {
    if (v) params.set(k, v);
  }
  if (period.preset !== DEFAULT_PRESET) params.set('period', period.preset);
  if (period.preset === 'custom') {
    if (period.from) params.set('from', period.from);
    if (period.to) params.set('to', period.to);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useTimePeriod(): {
  period: TimePeriod;
  setPreset: (preset: PresetId) => void;
  setCustomRange: (from: string, to: string) => void;
  /** Query suffix to forward the active period through internal links. */
  querySuffix: (extra?: Record<string, string>) => string;
} {
  const router = useRouter();

  const period: TimePeriod = useMemo(() => {
    const presetParam = (router.query.period as string | undefined) ?? DEFAULT_PRESET;
    const isValidPreset = PRESET_IDS.includes(presetParam as PresetId);
    const preset = (isValidPreset ? presetParam : DEFAULT_PRESET) as PresetId;
    if (preset === 'custom') {
      return {
        from: (router.query.from as string | undefined) ?? null,
        to: (router.query.to as string | undefined) ?? null,
        preset,
      };
    }
    const range = presetRange(preset);
    return { from: range.from, to: range.to, preset };
  }, [router.query.period, router.query.from, router.query.to]);

  const writeQuery = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      // Merge with current query and replace so we stay in the same history slot
      // when the user changes the range. We use `replace` so a single back-press
      // returns to whatever page they were on, not to an earlier filter.
      const next = { ...router.query, ...patch } as Record<string, string | undefined>;
      // Strip nulls/empty so the URL stays clean for the default state.
      for (const k of Object.keys(next)) {
        const v = next[k];
        if (v == null || v === '') delete next[k];
      }
      router.replace({ pathname: router.pathname, query: next }, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router],
  );

  const setPreset = useCallback(
    (preset: PresetId) => {
      if (preset === DEFAULT_PRESET) {
        // Clean URL for the default — no ?period=3m noise.
        writeQuery({ period: null, from: null, to: null });
      } else if (preset === 'custom') {
        // No range yet — caller must follow up with setCustomRange.
        writeQuery({ period: 'custom' });
      } else {
        writeQuery({ period: preset, from: null, to: null });
      }
    },
    [writeQuery],
  );

  const setCustomRange = useCallback(
    (from: string, to: string) => {
      writeQuery({ period: 'custom', from, to });
    },
    [writeQuery],
  );

  // Pull rubric off the URL so drilldown links carry it automatically. We
  // read it here (rather than via useRubric) to keep this hook the single
  // source of truth for the link suffix — callers don't need to remember to
  // pass the active rubric explicitly.
  const rubricParam = router.query.rubric;
  const rubricExtra: Record<string, string> =
    rubricParam === 'ECR' ? { rubric: 'ECR' } : {};
  // Carry the active lens across drilldown only when the user is authorized for it.
  // Without the flag check, a stale ?lens=gaps URL would silently propagate to users
  // who can't see the gaps lens.
  const access = useInsightsAccess();
  const lensParam = router.query.lens;
  const lensExtra: Record<string, string> = isAllowedGapsLens(lensParam, access)
    ? { lens: lensParam }
    : {};
  const querySuffix = useCallback(
    (extra: Record<string, string> = {}) =>
      periodQuerySuffix(period, { ...rubricExtra, ...lensExtra, ...extra }),
    [period, rubricExtra.rubric, lensExtra.lens],
  );

  return { period, setPreset, setCustomRange, querySuffix };
}

// Pretty-print a period for the dashboard headline. `Last 3 months` for
// presets, or "Feb 18 – May 18, 2026" for custom ranges.
export function formatPeriod(period: TimePeriod): string {
  if (period.preset !== 'custom') return presetLabel(period.preset);
  if (!period.from || !period.to) return 'Custom range';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const f = parseLocalDay(period.from);
  const t = parseLocalDay(period.to);
  if (f.getFullYear() === t.getFullYear()) {
    return `${f.toLocaleDateString('en-US', opts)} – ${t.toLocaleDateString('en-US', {
      ...opts,
      year: 'numeric',
    })}`;
  }
  return `${f.toLocaleDateString('en-US', {
    ...opts,
    year: 'numeric',
  })} – ${t.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

// Format for the hero sentence — fits the slot "scores improved by X% {here}".
// For preset windows we say "in the last 3 months". For "All time" we say
// "across all Curipod lessons". For custom we say "from Feb 18 – May 18".
export function formatPeriodInSentence(period: TimePeriod): string {
  if (period.preset === 'all') return 'across all Curipod lessons';
  if (period.preset === 'custom') {
    if (!period.from || !period.to) return 'in the selected period';
    return `between ${formatPeriod(period)}`;
  }
  if (period.preset === 'term') {
    // Spell the start date out ("since August 1") rather than naming the
    // period. Read off the resolved window rather than today's date so a
    // shared "?period=term" link reads the same as the dashboard it came from.
    const start = parseLocalDay(period.from ?? termStartIso(new Date()));
    return `since ${start.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    })}`;
  }
  if (period.preset === 'ytd') return 'since January 1';
  const dur =
    period.preset === '1w'
      ? 'week'
      : period.preset === '1m'
      ? 'month'
      : period.preset === '3m'
      ? '3 months'
      : period.preset === '6m'
      ? '6 months'
      : 'year';
  return `in the last ${dur}`;
}

// Format the per-bar subtitles in the hero ("First lesson average" /
// "Latest lesson average"). When the user has a from/to, we show those
// directly. When they're on "all time", we fall back to "across all data".
export function formatPeriodEndpoints(
  period: TimePeriod,
): {
  fromLabel: string;
  toLabel: string;
} {
  if (period.preset === 'all' || !period.from || !period.to) {
    return { fromLabel: 'Earliest lesson', toLabel: 'Most recent lesson' };
  }
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const f = parseLocalDay(period.from).toLocaleDateString('en-US', opts);
  const t = parseLocalDay(period.to).toLocaleDateString('en-US', opts);
  return { fromLabel: `From ${f}`, toLabel: `To ${t}` };
}

// The same captions for a single teacher's or class's page, read off its
// lesson list (oldest first) rather than the period, so they name the days
// the first and latest lessons were taught. Undefined when either date is
// missing, which leaves the hero on `formatPeriodEndpoints`.
export function lessonListEndpoints(
  lessons: { date: string | null }[],
): { fromLabel: string; toLabel: string } | undefined {
  const first = lessons[0]?.date;
  const last = lessons[lessons.length - 1]?.date;
  if (!first || !last) return undefined;
  return { fromLabel: `From ${first}`, toLabel: `To ${last}` };
}
